import { useMutation, useQueryClient } from '@tanstack/react-query';

import { PrependUrl } from 'enums';
import { Question, Rubric } from 'hooks/useGetCompetencyDetails';
import { fetchWithCsrfToken } from 'utils/fetchWrapper';


interface IUpdateCompetencyResponse {
  uid: string;
  name: string;
  description: string;
  weight: number;
  rubrics?: Rubric[];
  questions?: Question[];
}

interface IUpdateCompetencyVariables {
  competencyUid: string;
  name?: string;
  description?: string;
  weight?: number;
}

interface IUpdateCompetencyContext {
  previousCompetencyDetails?: IUpdateCompetencyResponse;
  newCompetencyDetails?: IUpdateCompetencyResponse;
}

const useUpdateCompetency = (interviewUid: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateCompetency,
    data,
    isLoading,
    error,
  } = useMutation<
    IUpdateCompetencyResponse,
    Error,
    IUpdateCompetencyVariables,
    IUpdateCompetencyContext
  >(
    ['updateAssessmentDetails'],
    async ({ competencyUid, name, description, weight }) => {
      const body = {
        uid: competencyUid,
        name,
        description,
        weight,
      };

      const assessment = await fetchWithCsrfToken<IUpdateCompetencyResponse>(
        `${PrependUrl.ASYNC}/competency/update`,
        'POST',
        body
      );

      return assessment;
    },
    {
      onSuccess: ({ uid: competencyUid }) => {
        queryClient.invalidateQueries({
          queryKey: ['getCompetencyDetails', interviewUid, competencyUid],
        });

        queryClient.invalidateQueries({
          queryKey: ['getAssessmentStepDetails', interviewUid],
        });
      },
      onMutate: async ({ competencyUid, name, description, weight }) => {
        await queryClient.cancelQueries([
          'getCompetencyDetails',
          interviewUid,
          competencyUid,
        ]);

        const previousCompetencyDetails =
          queryClient.getQueryData<IUpdateCompetencyResponse>([
            'getCompetencyDetails',
            interviewUid,
            competencyUid,
          ]);

        const newCompetencyDetails =
          queryClient.setQueryData<IUpdateCompetencyResponse>(
            ['getCompetencyDetails', interviewUid, competencyUid],
            {
              uid: competencyUid,
              name: name || previousCompetencyDetails?.name || '',
              description:
                description || previousCompetencyDetails?.description || '',
              weight: weight || previousCompetencyDetails?.weight || 0,
              rubrics: previousCompetencyDetails?.rubrics,
              questions: previousCompetencyDetails?.questions,
            }
          );

        return { previousCompetencyDetails, newCompetencyDetails };
      },
      onError: (_, variables, context) => {
        queryClient.setQueryData(
          ['getCompetencyDetails', interviewUid, variables.competencyUid],
          context?.previousCompetencyDetails
        );
      },
    }
  );

  return {
    updateCompetency,
    data,
    isLoading,
    error,
  };
};

export default useUpdateCompetency;
